import { ChevronLeftSolid, ChevronRightSolid } from '@allocab/allocab-icons';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';

type Props = {
  nbPages: number;
  className?: string;
};

export const Pagination = ({ nbPages, className }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Math.max(parseInt(searchParams.get('page') ?? '1'), 1);

  const handleChangePage = (page: number) => {
    if (page !== -1) {
      setSearchParams(params => {
        params.set('page', `${page}`);
        return params;
      });
    }
  };

  const pageShown = Array.from({ length: nbPages }).reduce<number[]>((acc, _, index) => {
    const page = index + 1;

    if (nbPages <= 7) return [...acc, page];

    if (currentPage <= 4) {
      if (page <= 5 || page === nbPages) {
        return [...acc, page];
      }
    } else if (nbPages - currentPage <= 3) {
      if (page >= nbPages - 4 || page === 1) {
        return [...acc, page];
      }
    } else {
      if (page === 1 || page === nbPages || Math.abs(page - currentPage) <= 1) {
        return [...acc, page];
      }
    }

    if (acc[acc.length - 1] !== -1) return [...acc, -1];

    return acc;
  }, []);

  useEffect(() => {
    if (nbPages && (currentPage > nbPages || currentPage < 1)) {
      setSearchParams(params => {
        params.set('page', `1`);
        return params;
      });
    }
  }, [currentPage, nbPages, setSearchParams]);

  return (
    <nav className={className}>
      <ul className="flex items-center -space-x-px h-10 text-base">
        <li>
          <button
            type="button"
            className="flex items-center justify-center px-4 h-10 ml-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-l-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
            onClick={() => handleChangePage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeftSolid height={16} width={16} className="fill-current" />
          </button>
        </li>
        {pageShown.map((page, index) => (
          <li key={index}>
            <button
              type="button"
              className={twJoin(
                'flex items-center justify-center px-4 h-10 leading-tight',
                currentPage === page
                  ? 'text-blue-600 border border-blue-300 bg-blue-50 hover:bg-blue-100 hover:text-blue-700 dark:border-gray-700 dark:bg-gray-700 dark:text-white'
                  : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white',
              )}
              onClick={() => handleChangePage(page)}
              disabled={page === -1 || page === currentPage}
            >
              {page === -1 ? '...' : page}
            </button>
          </li>
        ))}

        <li>
          <button
            type="button"
            className="flex items-center justify-center px-4 h-10 leading-tight text-gray-500 bg-white border border-gray-300 rounded-r-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
            onClick={() => handleChangePage(currentPage + 1)}
            disabled={currentPage === nbPages}
          >
            <ChevronRightSolid height={16} width={16} className="fill-current" />
          </button>
        </li>
      </ul>
    </nav>
  );
};
